import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
} from '@mui/material';
import { BorderColor, ViewInAr as CubeIcon, ArrowDropDown, MoreVert } from '@mui/icons-material';
import { useRouter, useHavePermission, usePaintRegionStatusLabels } from '@/hooks';
import { ConditionType, Permissions } from '@/types';
import { DataTable } from '@/components/shared/Table';
import {
  NameAndArea,
  UnitsEnum,
  UnitSystemEnum,
  AllPaintRegionsForPaintBlockTableDocument,
  PaintRegionStatus,
  useUpdatePaintRegionStatusMutation,
  useAllPaintRegionsForPaintBlockTableQuery,
  PointOfInterestFindingAndName,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { upperSnakeCase, parseNumberOrInfinity, convertTimeStampToDate } from '@/utils';
import { XlsxProcessor } from '@/utils/xlsx';
import { allPointOfInterestTemplates as allPointOfInterestTemplatesState } from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { primary } from '@/theme/colors';
import { PoiDialogBox } from './PoiDialogBox';
import { CreatePaintBlockReportModal } from './CreatePaintBlockReportModal';
import { RemediationDateModal } from './RemediationDateModal';
import { datePickerSlotProps } from '@/components/shared/ReportsRevamped/styles';
import { DatePicker } from '@mui/x-date-pickers';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog/ConfirmationDialog';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

type PointOfInterestTableProps = {
  template: Array<{ id: string; name: string }>;
  displayColumns: Array<string>;
  setPaintBlockTableXlsxProcessor: Dispatch<SetStateAction<XlsxProcessor | undefined>>;
  selectedConditionType: string;
  defects: Array<string> | undefined;
  defectsAreaByPaintBlocks: NameAndArea[];
  findingsByPaintBlock: PointOfInterestFindingAndName[] | undefined;
  structureName?: string;
};

type RowData = {
  [index: string]: string | number;
};

export const PaintBlockTable = ({
  defectsAreaByPaintBlocks = [],
  template = [],
  displayColumns = [],
  setPaintBlockTableXlsxProcessor,
  selectedConditionType,
  defects,
  findingsByPaintBlock,
  structureName,
}: PointOfInterestTableProps) => {
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const unitSystem = useAtomValue(state.unitSystem);
  const setSelectedAssemblyId = useSetAtom(state.selectedAssemblyId);
  const canAccess3DView = useHavePermission(Permissions.MANAGE_BETA_FEATURES);
  const allPointOfInterestTemplates = useAtomValue(allPointOfInterestTemplatesState);
  const [isCompletedSettingsOpen, setIsCompletedSettingsOpen] = useState(false);
  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState('');
  const [selectedRemediationDate, setSelectedRemediationDate] = useState<string | null>(null);
  const [isRemediationDateModalOpen, setIsRemediationDateModalOpen] = useState(false);
  const [selectedPaintBlockName, setSelectedPaintBlockName] = useState<string>('');
  const router = useRouter();
  const structureId = useGetStructureId();
  const [showTotalArea, setShowTotalArea] = useState(false);
  const [updatePaintRegionStatus] = useUpdatePaintRegionStatusMutation();
  const [isPoiDialogOpen, setIsPoiDialogOpen] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<{
    paintRegionId: string;
    status: PaintRegionStatus;
  } | null>(null);
  const { getPaintBlockLabels, statusOptions } = usePaintRegionStatusLabels();
  const selectedPaintRegionGroupId = useAtomValue(state.selectedPaintRegionGroupId);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [currentPaintBlockId, setCurrentPaintBlockId] = useState<string>('');
  const setSelectedPaintBlockHistory = useSetAtom(state.selectedPaintBlockHistory);

  const handleConfirmStatusUpdate = async () => {
    if (!pendingStatusUpdate) return;

    const today = new Date().toISOString();
    await updatePaintRegionStatus({
      variables: {
        input: {
          paintRegionId: pendingStatusUpdate.paintRegionId,
          status: pendingStatusUpdate.status,
          remediationDate: today,
          lastModifiedFor: 'Completion',
        },
      },
      refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
    });

    // Reset pending update
    setPendingStatusUpdate(null);
  };

  const handleCancelStatusUpdate = () => {
    setPendingStatusUpdate(null);
  };

  const handleRemediationDateConfirm = async (date: Date) => {
    if (!selectedPaintRegionId) return;

    const dateString = date.toISOString();

    // Check if we're updating status (from dropdown) or just the date (from edit button)
    const shouldUpdateStatus = pendingStatusUpdate?.status === PaintRegionStatus.PartiallyCompleted;

    // Update the paint region with the selected remediation date and optionally the status
    await updatePaintRegionStatus({
      variables: {
        input: {
          paintRegionId: selectedPaintRegionId,
          remediationDate: dateString,
          ...(shouldUpdateStatus && { status: PaintRegionStatus.PartiallyCompleted }),
        },
      },
      refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
    });

    // Clear pending status update if it exists
    if (shouldUpdateStatus) {
      setPendingStatusUpdate(null);
    }

    // Store the date and open the POI dialog
    setSelectedRemediationDate(dateString);
    setIsPoiDialogOpen(true);
  };

  const { data: paintRegionsData } = useAllPaintRegionsForPaintBlockTableQuery({
    variables: {
      input: { structureId },
    },
    fetchPolicy: 'cache-first',
  });

  const toggleCompletedSettingsOpen = useCallback((status: boolean) => {
    setIsCompletedSettingsOpen(status);
    setIsPoiDialogOpen(false);
  }, []);

  useEffect(() => {
    setShowTotalArea(selectedConditionType === ConditionType.COATING);
  }, [selectedConditionType]);

  useEffect(() => {
    const updatedValue: Record<string, boolean> = {};
    for (const field of template) {
      updatedValue[field.name.toLowerCase().replaceAll(' ', '_')] = displayColumns.includes(
        field.name.toLowerCase()
      );
    }

    // Only update if there are changes
    // this is a workaround to prevent unnecessary re-renders
    // TODO: find a better solution because this is clearly not good design
    if (JSON.stringify(updatedValue) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(updatedValue);
    }
  }, [displayColumns, template, visibleColumns]);

  const render3dViewCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const assemblyId = parameters.formattedValue;
      const handle3dView = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        setSelectedAssemblyId(assemblyId);
        router.push(`/analysis/${router.query.inspection}/viewer`);
      };

      return assemblyId ? (
        <Box>
          <Button
            data-testid={`3d-view-${assemblyId}`}
            sx={{ height: 1 }}
            startIcon={<CubeIcon sx={{ color: '#35C2A3' }} />}
            onClick={handle3dView}
          >
            3D
          </Button>
        </Box>
      ) : (
        <></>
      );
    },
    [router, setSelectedAssemblyId]
  );

  const updateStatus = useCallback(
    async (paintRegionId: string, status: string) => {
      await updatePaintRegionStatus({
        variables: {
          input: {
            paintRegionId: paintRegionId,
            status: upperSnakeCase(status) as PaintRegionStatus,
          },
        },
        refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
      });
    },
    [updatePaintRegionStatus]
  );

  const renderStatusCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const selection =
        paintRegionsData?.allPaintRegions?.find((item) => item.id === parameters.row.id)?.status ??
        PaintRegionStatus.InspectionPending;

      const handleChangeStatus = async (value: PaintRegionStatus) => {
        const paintRegionId = parameters?.row.id;
        setSelectedPaintRegionId(paintRegionId);

        // Handle non-remediation statuses
        if (
          value !== PaintRegionStatus.PartiallyCompleted &&
          value !== PaintRegionStatus.Completed
        ) {
          updateStatus(paintRegionId, value);
          return;
        }

        // Handle completed status with confirmation
        if (value === PaintRegionStatus.Completed) {
          setPendingStatusUpdate({ paintRegionId, status: value });
          setIsConfirmationModalOpen(true);
          return;
        }

        // Handle partially completed status - show date modal first
        if (value === PaintRegionStatus.PartiallyCompleted) {
          setSelectedPaintBlockName(parameters?.row.block || '');
          setPendingStatusUpdate({ paintRegionId, status: value });
          setIsRemediationDateModalOpen(true);
          return;
        }
      };

      return (
        <Stack direction="row" alignItems="center" width="fit-content" sx={{ gap: 1 }}>
          <FormControl>
            <Select
              inputProps={{ sx: { pt: '10px', pb: '7px' } }}
              defaultValue={selection}
              renderValue={() => {
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selection === PaintRegionStatus.Completed && (
                      <SvgIcon>
                        <CheckCircleIcon color="success" />
                      </SvgIcon>
                    )}
                    {getPaintBlockLabels(selection)}
                  </Box>
                );
              }}
              onChange={(event) => handleChangeStatus(event.target.value as PaintRegionStatus)}
              data-testid="select-continent-dropdown"
            >
              {statusOptions.map((option: { value: string; label: string }) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  onClick={() => handleChangeStatus(option.value as PaintRegionStatus)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selection === PaintRegionStatus.PartiallyCompleted && (
            <Button
              onClick={() => {
                setSelectedPaintRegionId(parameters?.row.id);
                setSelectedPaintBlockName(parameters?.row.block || '');
                setIsRemediationDateModalOpen(true);
              }}
              sx={{ minWidth: '24px', background: 'transparent' }}
            >
              <BorderColor
                sx={{
                  border: '1px solid',
                  borderColor: primary,
                  borderRadius: 1,
                  p: '4px',
                }}
              />
            </Button>
          )}
        </Stack>
      );
    },
    [paintRegionsData?.allPaintRegions, updateStatus, getPaintBlockLabels, statusOptions]
  );

  const renderCompletionDateCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (item) => item.id === parameters.row.id
      );
      const isCompleted =
        paintRegion?.status === PaintRegionStatus.Completed ||
        paintRegion?.status === PaintRegionStatus.PartiallyCompleted;
      const remediationDate = paintRegion?.remediationDate;

      const handleDateChange = async (newDate: Date | null) => {
        if (newDate && paintRegion?.id) {
          await updatePaintRegionStatus({
            variables: {
              input: {
                paintRegionId: paintRegion.id,
                remediationDate: newDate.toISOString(),
                ...(paintRegion.status === PaintRegionStatus.Completed && {
                  status: PaintRegionStatus.Completed,
                }),
              },
            },
            refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
          });
        }
      };

      const handleCellClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
      };

      const handleCellMouseDown = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
      };

      return (
        <Stack
          direction="row"
          alignItems="center"
          width="fit-content"
          sx={{ gap: 1 }}
          data-testid="remediation-date-cell"
          onClick={handleCellClick}
          onMouseDown={handleCellMouseDown}
        >
          <DatePicker
            value={convertTimeStampToDate(remediationDate) || null}
            onChange={handleDateChange}
            format="dd MMM yyyy"
            disabled={!isCompleted}
            slotProps={{
              ...datePickerSlotProps,
              textField: {
                ...datePickerSlotProps.textField,
                variant: 'outlined',
                placeholder: 'N/A',
                onClick: handleCellClick,
                onMouseDown: handleCellMouseDown,
              },
            }}
            slots={{
              openPickerIcon: ArrowDropDown,
            }}
            data-testid="remediation-date-picker"
          />
        </Stack>
      );
    },
    [paintRegionsData?.allPaintRegions, updatePaintRegionStatus]
  );

  const tableSorting = useCallback(
    (a: string | number | null | undefined, b: string | number | null | undefined) => {
      // Handle null/undefined/empty string cases
      if (
        (a === null || a === undefined || a === '') &&
        (b === null || b === undefined || b === '')
      )
        return 0;
      if (a === null || a === undefined || a === '') return 1;
      if (b === null || b === undefined || b === '') return -1;

      // Convert to numbers if they're strings
      const numA = typeof a === 'number' ? a : parseNumberOrInfinity(a);
      const numB = typeof b === 'number' ? b : parseNumberOrInfinity(b);

      // Handle NaN cases after conversion
      if (Number.isNaN(numA) && Number.isNaN(numB)) return 0;
      if (Number.isNaN(numA)) return 1;
      if (Number.isNaN(numB)) return -1;

      return numA - numB;
    },
    []
  );

  // Define renderAreaCell to convert values based on current unit system
  const renderAreaCell = useCallback(
    (params: GridRenderCellParams) => {
      // For display in the UI, convert based on unit system
      if (typeof params.value === 'number') {
        // Convert to appropriate unit system
        const displayValue =
          unitSystem === UnitSystemEnum.Imperial
            ? params.value * 10.7639 // Convert m² to ft²
            : params.value; // Keep as m²

        // Format the number with 2 decimal places
        return displayValue.toFixed(2);
      }
      return params.value || '0';
    },
    [unitSystem]
  );

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(() => {
    const fieldColumns: GridColDef[] = [
      {
        field: 'block',
        headerName: 'BLOCK#',
        type: 'string',
        flex: 1,
        sortComparator: tableSorting,
      },
      {
        field: 'findings',
        headerName: 'TOTAL FINDINGS',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: (params) => <div data-testid={`${params.row.block}-count`}>{params.value}</div>,
      },
    ];
    if (showTotalArea) {
      fieldColumns.push({
        field: 'corrodedArea',
        headerName: `Total Corroded Area ${
          unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)'
        }`,
        type: 'number',
        flex: 1,
        renderCell: renderAreaCell,
        sortComparator: tableSorting,
      });
    }
    for (const field of template) {
      const hasUnits = !Object.values(allPointOfInterestTemplates)
        .map((poiTemplate) => poiTemplate?.name.toUpperCase())
        .includes(field?.name?.toUpperCase());
      const units = unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)';
      const fieldName = field?.name.toLowerCase().replaceAll(' ', '_');

      fieldColumns.push({
        field: fieldName,
        headerName: `${field.name.toUpperCase()} ${hasUnits ? units : ''}`,
        type: 'number',
        flex: 1,
        renderCell: hasUnits ? renderAreaCell : undefined,
        sortComparator: tableSorting,
      });
    }
    if (canAccess3DView) {
      fieldColumns.push({
        headerName: 'VIEWS',
        field: 'assemblyId',
        type: 'string',
        flex: 1,
        renderCell: render3dViewCell,
      });
    }
    fieldColumns.push(
      {
        headerName: 'Status',
        field: 'status',
        type: 'custom',
        flex: 2.5,
        renderCell: renderStatusCell,
      },
      {
        headerName: 'COMPLETION DATE',
        field: 'remediationDate',
        type: 'custom',
        flex: 2,
        renderCell: renderCompletionDateCell,
      },
      {
        headerName: '',
        field: '',
        flex: 1,
        type: 'custom',
        renderCell: (value) => {
          const paintBlockId = value?.id.toString();
          const isMenuOpen = Boolean(menuAnchorEl) && currentPaintBlockId === paintBlockId;
          const selectedPaintBlockData = value.row;

          const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setMenuAnchorEl(event.currentTarget);
            setCurrentPaintBlockId(paintBlockId);
          };

          const handleMenuClose = () => {
            setMenuAnchorEl(null);
            setCurrentPaintBlockId('');
          };

          const handleCreateReport = () => {
            setSelectedPaintRegionId(paintBlockId);
            setIsReportsModalOpen(true);
            handleMenuClose();
          };

          const handleShowHistory = () => {
            setSelectedPaintBlockHistory(selectedPaintBlockData);
            handleMenuClose();
          };

          return (
            <>
              <IconButton onClick={handleMenuOpen} sx={{ height: '70%', width: 'fit-content' }}>
                <MoreVert />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleCreateReport}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>Create Report</Box>
                </MenuItem>
                <MenuItem onClick={handleShowHistory}>
                  Show History
                  <AbyssIcon iconName="beta" size={'sm'} style={{ marginLeft: '0.5rem' }} />
                </MenuItem>
              </Menu>
            </>
          );
        },
      }
    );

    return fieldColumns;
  }, [
    allPointOfInterestTemplates,
    canAccess3DView,
    render3dViewCell,
    renderStatusCell,
    renderCompletionDateCell,
    showTotalArea,
    tableSorting,
    template,
    unitSystem,
    renderAreaCell,
    menuAnchorEl,
    currentPaintBlockId,
    setSelectedPaintBlockHistory,
  ]);

  const rows: RowData[] = useMemo(() => {
    return (
      paintRegionsData?.allPaintRegions
        ?.filter((paintRegion) => {
          // if no group is selected, include all regions
          if (!selectedPaintRegionGroupId) return true;
          return paintRegion?.paintRegionGroup?.id === selectedPaintRegionGroupId;
        })
        .map((paintRegion) => {
          const corrodedArea = defectsAreaByPaintBlocks?.find(
            (area) => area.name === paintRegion.name
          );

          // Calculate total area as raw number for export
          const totalArea =
            corrodedArea?.totalArea?.reduce((accumulator, currentValue) => {
              return (
                accumulator +
                (defects?.includes(currentValue?.type.toLowerCase().replaceAll(' ', '_') ?? '')
                  ? currentValue?.value ?? 0
                  : 0)
              );
            }, 0) ?? 0;

          // Store raw numbers for export
          const corrodedAreaTypes: { [key: string]: number } = {};
          for (const areaType of corrodedArea?.totalArea ?? []) {
            if (areaType?.type && areaType?.value) {
              const type = areaType.type.toLowerCase().replaceAll(' ', '_');
              corrodedAreaTypes[type] =
                Number(
                  localiseMeasurement({
                    unitSystem,
                    sourceUnit: UnitsEnum.SquareMeter,
                    value: areaType.value,
                    roundToDecimal: 2,
                    showUnitLabel: false,
                  })
                ) ?? 0;
              corrodedAreaTypes[type] = areaType.value;
            }
          }

          const poiFindingByPaintBlock = findingsByPaintBlock?.find(
            ({ name }) => name === paintRegion.name
          );

          const rowData: RowData = {
            id: paintRegion.id,
            block: paintRegion.name,
            corrodedArea: totalArea ?? 0,
            findings: poiFindingByPaintBlock?.totalCount ?? 0,
            ...corrodedAreaTypes,
          };

          for (const fieldData of poiFindingByPaintBlock?.pointOfInterestFindings ?? []) {
            if (fieldData?.type && fieldData?.count) {
              rowData[fieldData.type.toLowerCase()] = fieldData.count;
            }
          }

          return rowData;
        }) ?? []
    );
  }, [
    paintRegionsData?.allPaintRegions,
    selectedPaintRegionGroupId,
    defectsAreaByPaintBlocks,
    unitSystem,
    findingsByPaintBlock,
    defects,
  ]);

  const cols = useMemo(() => {
    // Get visible columns in the correct order
    const visibleColumns = columns.filter((column) => !column.hideable);

    // Process each row to ensure data is in the correct column order and format
    const processedData = rows.map((row) => {
      return visibleColumns.map((col) => {
        const value = row[col.field];
        // Check if this is a column that should have units (uses renderAreaCell)
        const hasUnits = col.renderCell === renderAreaCell;

        if (hasUnits && typeof value === 'number') {
          // For export, we want the raw number in the current unit system
          // The value is stored in m², so we need to convert if using imperial
          if (unitSystem === UnitSystemEnum.Imperial) {
            return value * 10.7639; // Convert m² to ft²
          }
          return value;
        }
        return value ?? '';
      });
    });

    // Transpose the data for XLSX export (each column becomes an array of values)
    const exportedData = visibleColumns.map((col, colIndex) => ({
      label: col.headerName ?? 'no label',
      data: processedData.map((row) => row[colIndex]),
    }));

    // Add Platform Name column if structureName is available
    if (structureName && rows.length > 0) {
      exportedData.push({
        label: 'Platform Name',
        data: rows.map(() => structureName),
      });
    }

    return exportedData;
  }, [columns, rows, unitSystem, renderAreaCell, structureName]);

  const xlsx = useMemo(
    () =>
      new XlsxProcessor({
        columns: cols,
        sheetName: 'Paint Block Data',
      }),
    [cols]
  );

  useEffect(() => {
    setPaintBlockTableXlsxProcessor(xlsx);
  }, [xlsx, setPaintBlockTableXlsxProcessor]);

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        pageSizeOptions={[25, 50, 100]}
        selected={false}
        visibleColumns={visibleColumns}
        setVisiblecolumns={setVisibleColumns}
      />
      <RemediationDateModal
        isOpen={isRemediationDateModalOpen}
        onClose={() => setIsRemediationDateModalOpen(false)}
        onConfirm={handleRemediationDateConfirm}
        paintBlockName={selectedPaintBlockName}
      />
      <PoiDialogBox
        isOpen={isPoiDialogOpen || isCompletedSettingsOpen}
        toggleDialog={toggleCompletedSettingsOpen}
        paintRegionId={selectedPaintRegionId}
        remediationDate={selectedRemediationDate}
      />
      <CreatePaintBlockReportModal
        onClose={() => setIsReportsModalOpen(false)}
        open={isReportsModalOpen}
        paintRegionId={selectedPaintRegionId}
      />
      <ConfirmationDialog
        title="Confirm Status Update"
        description={`All components and POIs on the selected paint block will be set to ${getPaintBlockLabels(
          PaintRegionStatus.Completed
        ).toLowerCase()}. Do you want to continue?`}
        confirmButtonText={`Yes, Mark as ${getPaintBlockLabels(PaintRegionStatus.Completed)}`}
        cancelButtonText="Cancel"
        onConfirm={handleConfirmStatusUpdate}
        onCancel={handleCancelStatusUpdate}
        handleClose={() => setIsConfirmationModalOpen(false)}
        isOpen={isConfirmationModalOpen}
      />
    </>
  );
};
